/**
 * Staff displayed on the site. Bios and remaining pronouns come from a
 * separate form — drop new photos in assets/staff/primary and
 * optional hover alts in assets/staff/hover named by first name.
 */
import { staffImages } from "./staffPhotos";

export type StaffRole = "Professor" | "Head TA" | "STA" | "UTA";

export interface StaffMember {
  name: string;
  cslogin: string;
  pronouns: string;
  blurb: string;
  image: string;
  altImage: string;
  role: StaffRole;
}

export interface StaffGroup {
  title: string;
  members: StaffMember[];
}

const BIO_PLACEHOLDER = "Bio coming soon.";

function member(
  name: string,
  cslogin: string,
  pronouns = "",
  blurb = BIO_PLACEHOLDER,
  role: StaffRole = "UTA"
): StaffMember {
  return {
    name,
    cslogin,
    pronouns,
    blurb,
    role,
    ...staffImages(cslogin),
  };
}

export const staffGroups: StaffGroup[] = [
  {
    title: "Head Teaching Assistants",
    members: [
      member("Andy", "avd", "he/him", "I'm originally from the Netherlands. My CS specialty is Computer Graphics, especially pen- and touch-computing. I'm a foodie and love the outdoors: mountain- and road-biking, sea kayaking and hiking.", "Professor"),
      member("Paula", "psromero", "she/her", "Hiii!! I'm a junior from Miami, FL studying Design Engineering and CS. I love going to concerts, Venezuelan food, doing my own nails, and minions (I'm VERY excited about the theme this semester). I absolutely loved being a TA for CS15 last year, so I'm thrilled to be returning as an HTA, and I can't wait to meet you all!!!", "Head TA"),
      member("Cece", "cbartin", "she/her", "hihi! I'm Cece, I'm a senior from St. Louis, Missouri. I love hiking, sleeping, and CS15!! I can't wait to meet everyone this year 🥳🙈❤️", "Head TA"),
      member("Jasper", "jhall44", "he/him", "Helloooooo, my name is Jasper, and I am from Brooklyn, NY (LETS GO BROOKLYN NETS). I am a junior planning on majoring in either pure CS or CS-Econ (changes weekly). I also like Chelsea FC and everything prem related. I am in Brown Votes and TEDx here at Brown, and like making root beer floats at VDub. I also like 3 Headed Snake by Gunna and Weezy and Young Thug. Shout out CS15, I am dumb excited to meet yall!", "Head TA"),
      member("Jacob", "jgrandch", "he/him", "Hi, I’m Jacob, and I’m from Queens, NY! In my free time, I love creating/playing games, making art and taking care of my fish tanks. I’m also a big fan of the outdoors. I’m looking forward to working alongside you all!", "Head TA"),
    ],
  },
  {
    title: "STAs",
    members: [
      member(
        "Aura",
        "asukapan",
        "she/her",
        "Bello!! I'm Aura, a sophomore from Seattle planning to concentrate in Computational Biology and STS. Alongside TAing, I'm an analyst for BIP, culinary chair for TSA, and I also do CS research. Reach out to me about golf, ballet, good eats, and cool animals. I'm THRILLED for all of you to experience CS15!",
        "STA"
      ),
      member(
        "Claire",
        "chan48",
        "she/her",
        "Hi everyone! I'm Claire, a sophomore studying CS-Econ from Munster, Indiana (very close to Chicago)! I love playing tennis, fishkeeping, reading, listening to music, drinking boba, and frolicking around with friends. I'm so excited to be one of your UTA-STAs this semester; feel free to reach out to me about anything!",
        "STA"
      ),
    ],
  },
  {
    title: "Undergraduate Teaching Assistants",
    members: [
      member(
        "Alexia",
        "abodet",
        "she/her",
        "Hi everyone!! My name is Alexia and I'm a junior originally from Shanghai and Paris, but I currently live in Mclean, VA. I study CS & HCI but in my free time, you can find me making something new as a monitor for the BDW or working on my latte art as a barista at the Underground! I love thrifting clothes with fun buttons, elevator jazz music, and polka dots. Excited to work with you all!! <3"
      ),
      member(
        "Arya",
        "avishwak",
        "she/her",
        "HELLOOOOO CS15 minions! I'm a sophomore studying pure math + cs and I'm from central Jersey (yes it exists). I like riding RIPTA buses and eating Andrews dry noodle bowls, and I'm on the Opinions team of the Brown Daily Herald. CS15 introduced me to cool people all around campus and broadened my vision of what I could achieve at Brown. I know it will for you too: this class will give you direction AND magnitude. OH YEAH!!!"
      ),
      member(
        "Beam",
        "rthampar",
        "he/him",
        "Hi everyone! I'm a junior from Bangkok, Thailand, studying APMA-Econ. I'm a huge foodie, music enjoyer, and tennis enthusiast. Super excited to be TAing for all of you!!!"
      ),
      member(
        "Daniel",
        "dschiffm",
        "he/him",
        "Hey everyone, I'm Daniel! I'm a senior studying applied math and computer science where I focus on tech policy topics. I'm from Maplewood, New Jersey (you can take the girl out of Jersey but you can't take the Jersey out of the girl). I love reading, running, playing basketball, and rock music. So excited for all of you to experience CS15!"
      ),
      member(
        "Duru",
        "zhuseyni",
        "she/her",
        "Hi everyone! ! I'm a senior from Izmir, Turkey studying CS and APMA-Econ. I really like baking/cooking, playing the piano, and reading. Growing up in the Mediterranean, I love olive, olive oil dishes, and small plates with a big table of friends. I am also obsessed with cs15; this is my 3rd year in the staff and am SO excited for this semester - feel free to reach out to say hi or chat about anything!!"
      ),
      member(
        "Isabella",
        "irszabo",
        "she/her",
        "Hi! I'm Isabella, and I'm a senior from Roswell, Georgia studying computer science and Hispanic studies. I love playing guitar, crocheting, reading, baking, and exploring all of Providence's coffee shops. I'm so excited to be one of your TAs this year!"
      ),
      member(
        "Jessi",
        "jshin108",
        "she/her",
        "Hello! I'm a junior from South Korea studying Computer Science and Visual Arts. In my free time, I love playing video games (Silksong most recently), crocheting, and recently started trying some nail art. So so excited to meet everyone :D"
      ),
      member(
        "Julius",
        "jmhochbe",
        "he/him",
        "Hello! I am a senior studying Physics and Engineering from Worcester, Massachusetts. Some of my favorite things to do: making music, playing card games, watching movies, cooking for my friends, and learning about quantum mechanics. CS15 has been such an amazing part of my Brown experience. I am unbelievably excited to be your TA this semester!"
      ),
      member(
        "Keanu",
        "kherft",
        "he/him",
        "OOH YEAH! I'm Keanu, a sophmore from Colombo, LK and Adelaide, AU (both places with the best beaches) and I'm studying Electrical Engineering and potentially English. I love physical and outdoor activities - if you're interested in doing a triathlon, you're looking at the right person! I also love going to the beach and befriending animals - all kinds! I'm super duper excited to be TAing CS15 and I can't wait for you guys to experience the amazingness of the class for yourselves! OOH YEAHH!!!"
      ),
      member(
        "Lana",
        "lyangmac",
        "she/her",
        "Hi everyone! I'm a 5th Year Master's student in computer science from Ann Arbor, Michigan. I love to run and hike outside, cook, and read. I am SO excited to be TAing CS15 again and cannot wait to meet you all!!"
      ),
      member(
        "Malcolm",
        "mhembre",
        "he/him",
        "I am a sophomore, computational biology major from Indianapolis! I am an Outing Club leader, certified EMT, and enjoy biking, hiking, and playing/watching soccer in my free time. Excited to meet all the new minions!"
      ),
      member(
        "Matty",
        "mberman8",
        "he/him",
        "Hi everyone!! I'm Matty and I'm a sophomore from Toronto, Canada studying Applied Math-Economics (very undecided though..). I love climbing random things on campus, collecting vinyl records, and am currently learning to figure skate! I'm SUPER excited to be TAing CS15 and perform in the skits!!"
      ),
      member(
        "Mjellma",
        "mmisini",
        "she/her",
        "Hi! I'm a sophomore from Kosovo concentrating on APMA-CS. I love reading, and swimming  far out into the sea. I'm very excited to meet everyone!"
      ),
      member("Ross", "rmgoldba", "he/him", "Hey everyone! I'm Ross and I'm a senior from the Chicago suburbs studying Computer Engineering. I love robotics, game dev, coding outside, and all kinds of music. I'm SO excited to meet you all and have another amazing semester of CS15!!"),
      member(
        "Samyak",
        "sjain80",
        "he/him",
        "Hi everyone! I am Samyak, a junior from Singapore studying CS-Economics. I love any flavored coffee drinks (Nitro Bar near Brown!) and look forward to hearing your coffee orders during office hours :)"
      ),
      member(
        "Sidd",
        "snareddy",
        "he/him",
        "Hi! I am a junior from Boulder, CO studying Computational Biology on the CS track. I love spending time in the outdoors whether that be skiing, hiking, or playing tennis. I'm super excited to meet everyone and have an amazing semester of CS15!"
      ),
      member(
        "Stephen",
        "syang195",
        "he/him",
        "I'm a senior from Ithaca, New York studying computational biology. In my free time I like to play saxophone, and watch hockey! Super excited to work with everyone this year. Please feel free to reach out about anything!"
      ),
      member(
        "Victoria",
        "vlhernan",
        "she/her",
        "Hiii I'm a senior from Irvine, CA studying APMA-CS. I love watching Love Island, cooking new recipes, and shopping. I loved this course so much I had to come back and TA it 3 times! I hope you'll end up loving this course as much as I do <3"
      ),
      member(
        "Will",
        "wjbernst",
        "he/him",
        "Hi, I'm Will! I am a junior from Brookline, MA, concentrating in APMA-CS. I am a diehard (Boston) sports fan and love to hike, ski, and play basketball (defending IM basketball champ). I can't wait to get to work with you all this semester!"
      ),
    ],
  },
];
